import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActive from 'src/components/Badge/BadgeActive';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import { convertEnergyPrice } from 'src/util/conversions';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactElement} - AdminVolumeChargeList component
 */
function AdminVolumeChargeList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { console.log(error); },
    );
  };

  if (!props) {
    return <Loading />;
  }

  const { relay, viewer } = props;
  const { volumeCharges } = viewer;

  const intl = useIntl();

  const copyLoadMore = intl.formatMessage({ id: 'admin.admin_volume_charge.admin_volume_charge_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'admin.admin_volume_charge.admin_volume_charge_list.load_more_button.state_no_more.label', defaultMessage: 'No more volume charges to load' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Enosi Admin - Volume Charges</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: 'Admin', path: '/admin' },
          { name: 'Volume Charges' },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">Volume Charges</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Title</th>
                <th scope="col">Identifier</th>
                <th scope="col">ID</th>
                <th scope="col">
                  <abbr title="External Identifier">Ext ID</abbr>
                </th>
                <th scope="col">Rate</th>
                <th scope="col">Tariff</th>
              </tr>
            </thead>
            <tbody>
              {
                // volumeCharges && <pre>{JSON.stringify(volumeCharges, null, 2)}</pre>
                volumeCharges && volumeCharges.edges && volumeCharges.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    id, title, identifier, externalIdentifier, rate, tariff,
                  } = node;

                  return (
                    <tr key={`admin-volume-charge-${id}`}>
                      <th scope="row" colSpan={2}>
                        <Link to={`/admin/volume-charges/${id}`}>
                          {title}
                        </Link>
                        <br />
                        {identifier}
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={id} />
                        <br />
                        {externalIdentifier}
                      </td>
                      <td>
                        {convertEnergyPrice(rate).toFixed(3)}
                        {' '}
                        <abbr title="cents per kilowatt hour">c/kWh</abbr>
                      </td>
                      <td>
                        {tariff && (
                          <>
                            <Link to={`/admin/tariffs/${tariff.id}`}>
                              {tariff.title}
                            </Link>
                            <br />
                            <BadgeActive
                              start={tariff.active.start}
                              finish={tariff.active.finish}
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminVolumeChargeList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminVolumeChargeList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminVolumeChargeList,
  {
    viewer: graphql`
      fragment AdminVolumeChargeList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        volumeCharges(
          first: $count
          after: $cursor
        ) @connection(key: "AdminVolumeChargeList_volumeCharges") {
          edges {
            cursor
            node {
              id
              externalIdentifier
              identifier
              title
              rate
              tariff {
                id
                title
                active { start finish }
              }
            }
          }
          count
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.volumeCharges;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminVolumeChargeList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminVolumeChargeList_viewer
      }
    }
  `,
  },
);
